import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { LIKE, EQ, CONTAINS } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import SelectInput from "../../framework/SelectInput";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import NumberInput from "../../framework/NumberInput";
import { isEmpty } from "../../utils/utils";
import {
  searchTransportByShortCode,
  searchTransportByName,
} from "../../services/transport-services";

import { TransportModes } from "../../utils/constant";

const TransportReportFilter = ({
  reportRef,
  onFilterShow,
  applyFilterOnMount = true,
}) => {
  const [pincode, setPincode] = useState(null);
  const [mobileNumber, setMobileNumber] = useState("");
  const [transportMode, setTransportMode] = useState();
  const [limit, setLimit] = useState(100);
  const [transportCode, setTransportCode] = useState("");
  const [transportName, setTransportName] = useState("");
  const [transportCodeOptions, setTransportCodeOptions] = useState([]);
  const [transportNameOptions, setTransportNameOptions] = useState([]);

  const [orderBy] = useState([{ fieldName: "shortCode", orderType: "asc" }]);

  const apr_filter_transport_code_ref = useRef();
  const applyFilterRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};

      if (!isEmpty(transportCode)) {
        filters.push(
          createFilterParam("shortCode", transportCode, "string", LIKE)
        );
      }
      if (!isEmpty(transportName)) {
        filters.push(
          createFilterParam("transportName", transportName, "string", LIKE)
        );
      }
      if (!isEmpty(pincode)) {
        filters.push(createFilterParam("pincode", pincode, "string", CONTAINS));
      }
      if (!isEmpty(mobileNumber)) {
        filters.push(
          createFilterParam("mobileNumber", mobileNumber, "string", CONTAINS)
        );
      }
      if (!isEmpty(transportMode)) {
        filters.push(
          createFilterParam(
            "transportMode",
            transportMode?.label,
            "string",
            LIKE
          )
        );
      }
      if (isFilterChanged) {
        result.filters = filters;
      }
      if (orderBy?.length) {
        result.orderBy = orderBy;
      }
      if (!isEmpty(limit)) {
        result.limit = limit;
      }
      reportRef.current?.applyFilters(result);
    },
    [
      transportCode,
      transportName,
      pincode,
      mobileNumber,
      transportMode,
      orderBy,
      limit,
      reportRef,
    ]
  );
  let debounce = useCallback(
    _.debounce(() => {
      applyFilterHandler();
    }, 300),
    []
  );

  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_transportCode"
            label="Short Code"
            value={transportCode}
            ref={apr_filter_transport_code_ref}
            options={transportCodeOptions}
            onChange={(e, value) => {
              setTransportCode(value?.shortCode);
            }}
            isOptionEqualToValue={(option, value) =>
              option?.shortCode === value?.shortCode
            }
            onFocus={() => setCurrentComponent("apr_filter_transportCode")}
            getOptionLabel={(option) => option.shortCode || option}
            disableClearable={false}
            freeSolo
            onInputChange={(e) => {
              if (!e) return;
              setTransportCode(e.target.value);
              setTransportCodeOptions([]);
              const query = `shortCode=${e.target.value}`;
              if (e.target.value?.length > 0) {
                searchTransportByShortCode(query).then((data) => {
                  setTransportCodeOptions(data || []);
                });
              }
            }}
            noOptionsText="Search Short Code here..."
            markers={JSON.stringify({
              up: "apr_filter_transportCode",
              down: "apr_filter_transportName",
              right: "apr_filter_transportName",
              left: "apr_filter_transportCode",
            })}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_transportName"
            label="Transporter Name"
            value={transportName}
            onChange={(e, value) => {
              setTransportName(value?.transportName);
            }}
            onFocus={() => setCurrentComponent("apr_filter_transportName")}
            options={transportNameOptions}
            getOptionLabel={(option) => option.transportName || option}
            disableClearable={false}
            freeSolo
            onInputChange={(e) => {
              if (!e) return;
              setTransportName(e.target.value);
              setTransportNameOptions([]);
              const query = `transportName=${e.target.value}`;
              if (e.target.value?.length > 0) {
                searchTransportByName(query).then((data) => {
                  setTransportNameOptions(data || []);
                });
              }
            }}
            noOptionsText="Search Transporter Name here..."
            markers={JSON.stringify({
              up: "apr_filter_transportCode",
              down: "apr_filter_transportMode",
              right: "apr_filter_transportMode",
              left: "apr_filter_transportCode",
            })}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="apr_filter_transportMode"
            label="Transport Mode"
            options={TransportModes}
            value={transportMode}
            disableClearable={false}
            onChange={(e, value) => setTransportMode(value)}
            onFocus={() => setCurrentComponent("apr_filter_transportMode")}
            markers={JSON.stringify({
              up: "apr_filter_transportName",
              down: "apr_filter_pincode",
              left: "apr_filter_transportName",
              right: "apr_filter_pincode",
            })}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="apr_filter_pincode"
            label="Pincode"
            value={pincode}
            onChange={(e) => setPincode(e.target.value)}
            onFocus={() => setCurrentComponent("apr_filter_pincode")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_transportMode",
                down: "apr_filter_mobileNumber",
                right: "apr_filter_mobileNumber",
                left: "apr_filter_transportMode",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="apr_filter_mobileNumber"
            label="Mobile Number"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            onFocus={() => setCurrentComponent("apr_filter_mobileNumber")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_pincode",
                down: "apr_filter_limit",
                right: "apr_filter_limit",
                left: "apr_filter_pincode",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>

      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="apr_filter_limit"
            label="Limit"
            value={limit}
            //onChange={(e) => setLimit(e.target.value)}
            onValueChange={(values) => setLimit(values.floatValue)}
            onFocus={() => setCurrentComponent("apr_filter_limit")}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "apr_filter_mobileNumber",
                down: "apr_apply_filter_button",
                right: "apr_apply_filter_button",
                left: "apr_filter_mobileNumber",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="apr_apply_filter_button"
            label="Apply"
            ref={applyFilterRef}
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => {
              applyFilterHandler(true);
            }}
            markers={JSON.stringify({
              up: "apr_filter_limit",
              left: "apr_filter_limit",
              right: "apr_apply_filter_button",
              down: "apr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  useEffect(() => {
    if (onFilterShow) {
      apr_filter_transport_code_ref?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);
  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);
  useEffect(() => {
    if (applyFilterOnMount) debounce();
    return () => {
      debounce.cancel();
    };
  }, [applyFilterOnMount, debounce]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default TransportReportFilter;
